import React, { memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, StarIcon, StarFilledIcon } from '@radix-ui/react-icons';
import { useMsal } from '@azure/msal-react';
import { RootState } from '../../store/store';
import { setSelectedModel, setSelectedTool } from '../../store/chatModeSlice';
import { getToolForModelSelection, getVisibleModelOptions, MODEL_OPTIONS, DEFAULT_STANDARD_MODEL_ID } from '../../utils/modelConfig';
import { useTheme } from '../Theme/ThemeProvider';
import { ModelConfig } from '../../utils/modelConfig';
import { createUserPreferencesService } from '../../utils/userPreferences.service';
import { setUserPreferences } from '../../store/userPreferencesSlice';
import logger from '../../utils/logger';

const FAVORITABLE_MODEL_IDS = new Set(['gpt-5.4-fast', 'gpt-5.4-reasoning', 'claude-sonnet-4.6-fast', 'claude-opus-4.7-reasoning']);
const FAVORITE_MODEL_STORAGE_KEY = 'favoriteModelId';

interface ModelSelectorProps {
visible: boolean;
  /** When provided, called instead of directly dispatching — allows the parent to run guards (e.g. file-upload warnings). */
  onModelSelect?: (modelId: string) => void;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ visible, onModelSelect }) => {
  const dispatch = useDispatch();
  const { instance: msalInstance } = useMsal();
  const selectedModelId = useSelector((state: RootState) => state.chatMode.selectedModelId);
  const activeTool = useSelector((state: RootState) => state.chatMode.selectedTool);
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const [favoriteModelId, setFavoriteModelId] = React.useState<string>(() => {
    return localStorage.getItem(FAVORITE_MODEL_STORAGE_KEY) ?? DEFAULT_STANDARD_MODEL_ID;
  });
  const [hoveredStarId, setHoveredStarId] = React.useState<string | null>(null);

  const visibleModels = getVisibleModelOptions(activeTool);
  const selectedModel =
    MODEL_OPTIONS.find(m => m.id === selectedModelId)
    ?? visibleModels.find(m => m.id === selectedModelId)
    ?? visibleModels[0];

  const handleStarClick = React.useCallback((e: React.MouseEvent, modelId: string) => {
    e.stopPropagation();
    setFavoriteModelId(modelId);
    localStorage.setItem(FAVORITE_MODEL_STORAGE_KEY, modelId);
    dispatch(setUserPreferences({ modelPref: modelId }));
    createUserPreferencesService(msalInstance)
      .updateUserPreferences({ modelPref: modelId })
      .then(() => logger.log('[UserPreferences] Model preference saved successfully:', modelId))
      .catch(err => { console.error('[UserPreferences] Failed to save model preference:', err); });
  }, [dispatch, msalInstance]);

  const handleModelSelection = React.useCallback((modelId: string) => {
    const toolForModel = getToolForModelSelection(modelId);
    if (onModelSelect) {
      onModelSelect(modelId);
    } else {
      if (toolForModel !== activeTool) {
        dispatch(setSelectedTool(toolForModel));
      }
      dispatch(setSelectedModel(modelId));
    }
    setIsOpen(false);
  }, [dispatch, onModelSelect, activeTool]);

  // Close on outside click
  React.useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  React.useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Close dropdown when hiding
  React.useEffect(() => {
    if (!visible && isOpen) {
        setIsOpen(false)
    };
  }, [isOpen, visible]);

  return (
    <div ref={containerRef} className="relative inline-block" data-tutorial="model-selector" style={{ zIndex: 100, visibility: visible ? 'visible' : 'hidden', pointerEvents: visible ? 'auto' : 'none' }}>
      {/* Trigger button */}
      <button
        onClick={() => {
            setIsOpen(prev => !prev)
        }}
        className="flex items-center justify-between gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors"
        style={{
          minWidth: '220px',
          maxWidth: '260px',
          color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#5E95ED',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #5E95ED',
          background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E8F3FF',
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span style={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
          <span className="top-heavy" style={{ flexShrink: 0 }}>Model:&nbsp;&nbsp;</span>
          <span style={{ display: 'flex', alignItems: 'center', color: isDark ? '#82B6FA' : '#2563EB' }}>
            {selectedModel.icon && <span style={{ display: 'inline-flex', marginRight: '4px', color: isDark ? '#82B6FA' : '#2563EB' }}>{selectedModel.icon}</span>}
            <span className="top-heavy">{selectedModel.shortName ?? selectedModel.displayName}</span>
          </span>
        </span>
        <ChevronDownIcon
          className="w-4 h-4 transition-transform flex-shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* Dropdown menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.8, y: -4 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleY: 0.8, y: -4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 mt-1 rounded-lg shadow-lg overflow-hidden origin-top py-1"
            style={{
              minWidth: '220px',
              width: containerRef.current?.offsetWidth ?? 'auto',
              background: isDark ? 'rgba(30, 30, 30, 0.95)' : '#FFFFFF',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #5E95ED',
              backdropFilter: 'blur(12px)',
            }}
            role="listbox"
            aria-activedescendant={selectedModelId}
          >
            {visibleModels.map((model: ModelConfig) => {
              const isSelected = model.id === selectedModelId;
              return (
                <button
                  key={model.id}
                  id={model.id}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleModelSelection(model.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                  style={{
                    background: isSelected
                      ? 'var(--color-secondary)'
                      : 'transparent',
                    borderTop: isSelected ? (isDark ? '1px solid rgba(82, 160, 255, 0.3)' : '1px solid #93c5fd') : '1px solid transparent',
                    borderBottom: isSelected ? (isDark ? '1px solid rgba(82, 160, 255, 0.3)' : '1px solid #93c5fd') : '1px solid transparent',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(82, 160, 255, 0.18)' : '#93c5fd'; }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isSelected
                      ? ('var(--color-secondary)')
                      : 'transparent';
                  }}
                >
                  {FAVORITABLE_MODEL_IDS.has(model.id) && (() => {
                    const isFavorite = favoriteModelId === model.id;
                    return (
                      <div
                        role={isFavorite ? undefined : 'button'}
                        tabIndex={isFavorite ? undefined : 0}
                        onClick={(e) => { if (!isFavorite) handleStarClick(e, model.id); else e.stopPropagation(); }}
                        onKeyDown={(e) => { if (!isFavorite && (e.key === 'Enter' || e.key === ' ')) handleStarClick(e as any, model.id); }}
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          if (!isFavorite) {
                            setHoveredStarId(model.id);
                            const row = e.currentTarget.parentElement as HTMLElement | null;
                            if (row) row.style.background = 'transparent';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.stopPropagation();
                          setHoveredStarId(null);
                          const row = e.currentTarget.parentElement as HTMLElement | null;
                          if (row && row.contains(e.relatedTarget as Node)) {
                            row.style.background = isDark ? 'rgba(82, 160, 255, 0.18)' : '#93c5fd';
                          }
                        }}
                        className="flex-shrink-0 flex items-center transition-all"
                        title={isFavorite ? 'Current favorite' : 'Set as favorite'}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          paddingRight: '4px',
                          cursor: isFavorite ? 'default' : 'pointer',
                          transform: hoveredStarId === model.id ? 'scale(1.15)' : 'scale(1)',
                          transition: 'transform 0.15s',
                        }}
                      >
                        <span style={{
                          display: 'inline-flex',
                          color: isFavorite
                            ? '#fbbf24'
                            : hoveredStarId === model.id
                              ? 'rgba(251, 191, 36, 0.55)'
                              : (isDark ? 'rgba(255,255,255,0.35)' : '#d1d5db'),
                          filter: hoveredStarId === model.id ? 'drop-shadow(0 0 1px #b45309)' : 'none',
                          transition: 'color 0.15s, filter 0.15s',
                        }}>
                          {(isFavorite || hoveredStarId === model.id)
                            ? <StarFilledIcon className="w-4 h-4" />
                            : <StarIcon className="w-4 h-4" />}
                        </span>
                      </div>
                    );
                  })()}
                  {/* Divider between star and model content */}
                  {/* {FAVORITABLE_MODEL_IDS.has(model.id) && (
                    <span style={{
                      display: 'inline-block',
                      width: '1px',
                      height: '20px',
                      background: 'var(--color-accent)',
                      flexShrink: 0,
                      marginRight: '2px',
                    }} />
                  )} */}
                  {model.icon && <span className="flex-shrink-0" style={{ color: isSelected ? (isDark ? '#82B6FA' : '#2563EB') : (isDark ? 'rgba(255,255,255,0.85)' : '#374151') }}>{model.icon}</span>}
                  <span className="flex flex-col">
                    <span className="top-heavy" style={{ display: 'inline-block', fontWeight: isSelected ? 600 : 400, color: isSelected ? (isDark ? '#82B6FA' : '#2563EB') : (isDark ? 'rgba(255,255,255,0.85)' : '#374151') }}>{model.shortName ?? model.displayName}</span>
                    {model.description && <span style={{fontWeight: isSelected ? 600 : 400, fontSize: '0.72rem', color: isDark ? 'rgba(255,255,255,0.45)' : '#9ca3af' }}>{model.description}</span>}
                  </span>
                </button>
              );
            })}

            {/* Footer hint */}
            <div style={{ borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb', margin: '4px 0 0' }} />
            <div className="px-3 py-1">
              <span style={{ fontSize: '0.7rem', color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }}>
                <StarFilledIcon className="w-3.5 h-3.5" style={{ color: '#fbbf24', display: 'inline', verticalAlign: 'middle', marginRight: '3px', position: 'relative', top: '-1px' }} />
                indicates your default model when opening 3M Navigator
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(ModelSelector);
